import type { SavedDataTableView } from 'src/features/data-table-views';
import { LOCAL_STORAGE_KEYS } from 'src/constants/local-storage';
import {
  getActiveDataTableViewId,
  setActiveDataTableViewId,
  useSavedDataTableViews,
} from 'src/features/data-table-views';

import type { TaxEnrollmentStatus, TaxEnrollmentTypeName } from '@savvy/constants/generated/gql-enums';

import type { TaxYear } from '../tax-overview-url-state';

export type SavedTaxFilterFields = {
  enrollmentTypeFilter: TaxEnrollmentTypeName[];
  statusFilter: TaxEnrollmentStatus[];
  taxYearFilter: TaxYear[];
};

export type SavedTaxFilter = SavedDataTableView<SavedTaxFilterFields>;

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isSavedTaxFilterFields(value: unknown): value is SavedTaxFilterFields {
  if (!isRecord(value)) return false;
  return (
    isStringArray(value['enrollmentTypeFilter']) &&
    isStringArray(value['statusFilter']) &&
    isStringArray(value['taxYearFilter'])
  );
}

export function getActiveTaxFilterPresetId(): string | null {
  return getActiveDataTableViewId(LOCAL_STORAGE_KEYS.TAX_ACTIVE_FILTER_PRESET_ID);
}

export function setActiveTaxFilterPresetId(id: string | null): void {
  setActiveDataTableViewId({ storageKey: LOCAL_STORAGE_KEYS.TAX_ACTIVE_FILTER_PRESET_ID, id });
}

export type UseSavedTaxFiltersReturn = {
  savedFilters: SavedTaxFilter[];
  atLimit: boolean;
  saveFilter: (name: string, filters: SavedTaxFilterFields) => SavedTaxFilter | null;
  updateFilter: (id: string, filters: SavedTaxFilterFields) => void;
  renameFilter: (id: string, name: string) => void;
  deleteFilter: (id: string) => SavedTaxFilter[];
};

export function useSavedTaxFilters(): UseSavedTaxFiltersReturn {
  const { savedViews, atLimit, saveView, updateView, renameView, deleteView } = useSavedDataTableViews({
    storageKey: LOCAL_STORAGE_KEYS.TAX_SAVED_FILTERS,
    filtersValidator: isSavedTaxFilterFields,
  });

  return {
    savedFilters: savedViews,
    atLimit,
    saveFilter: saveView,
    updateFilter: updateView,
    renameFilter: renameView,
    deleteFilter: deleteView,
  };
}
